import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Clock, Star, ExternalLink, ArrowUpRight } from "lucide-react";
import { OUTLETS, MAP_DIRECTIONS_URL } from "@/data";
import { scrollToId } from "@/lib/scroll";
import { Reveal, MaskLine } from "@/components/Reveal";

export const Outlets = () => {
  const mapRef = useRef(null);

  useEffect(() => {
    if (mapRef.current) return;
    const outlet = OUTLETS[0];
    mapRef.current = L.map("outlet-map", { scrollWheelZoom: false }).setView([outlet.lat, outlet.lng], 14);
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(mapRef.current);
    L.marker([outlet.lat, outlet.lng], {
      icon: L.divIcon({ className: "fp-pin-wrap", html: '<span class="fp-pin"></span>', iconSize: [18, 18], iconAnchor: [9, 9] }),
    })
      .addTo(mapRef.current)
      .bindTooltip("French Pie — Bemina", { direction: "top", offset: [0, -12] });
    return () => {
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, []);

  const o = OUTLETS[0];

  return (
    <section id="outlets" data-testid="outlets-section" className="py-24 lg:py-36 bg-[#F8F3EA]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-16 lg:mb-20">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="eyebrow mb-5">Nos Maisons</p>
            </Reveal>
            <h2 className="font-display font-medium leading-[1.02] tracking-tight text-4xl sm:text-5xl lg:text-6xl">
              <MaskLine>Find us in</MaskLine>
              <MaskLine delay={0.12}>
                <span className="italic text-[#C85A32]">Srinagar.</span>
              </MaskLine>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <Reveal>
              <p className="text-base leading-relaxed text-[#1C130E]/70 max-w-sm">
                Our first maison is open at Havells Galaxy, Bemina — baking in-house from dawn. More across Kashmir
                soon.
              </p>
            </Reveal>
          </div>
        </div>

        <Reveal>
          <div className="rounded-3xl overflow-hidden border border-[rgba(28,19,14,0.12)] shadow-[0_30px_60px_-30px_rgba(28,19,14,0.3)] relative z-0 mb-8 lg:mb-12">
            <div id="outlet-map" data-testid="outlet-map" className="w-full h-[380px] lg:h-[500px] relative z-0" />
          </div>
        </Reveal>

        <Reveal>
          <div
            data-testid={`outlet-card-${o.id}`}
            className="group grid grid-cols-1 lg:grid-cols-2 gap-10 rounded-3xl border border-[rgba(28,19,14,0.12)] bg-[#FDFBF7] p-8 lg:p-12 transition-all duration-500 hover:shadow-[0_28px_50px_-25px_rgba(28,19,14,0.35)] hover:border-[#C85A32]/40"
          >
            <div>
              <div className="flex items-center justify-between">
                <p className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-[#C85A32]">
                  {o.city} · Jammu &amp; Kashmir
                </p>
                <span className="text-[10px] font-mono-accent uppercase tracking-wider bg-[#1C130E] text-[#FDFBF7] px-2.5 py-1 rounded-full">
                  {o.badge}
                </span>
              </div>
              <h3 className="font-display font-medium text-4xl lg:text-5xl mt-4">{o.area}</h3>
              <div className="mt-6 space-y-3 text-sm text-[#1C130E]/70">
                <p className="flex items-start gap-2">
                  <MapPin size={14} className="text-[#C85A32] shrink-0 mt-0.5" />
                  {o.address}
                </p>
                <p className="flex items-center gap-2">
                  <Clock size={14} className="text-[#C85A32] shrink-0" />
                  {o.hours}
                </p>
                <p className="flex items-center gap-2">
                  <Star size={14} className="text-[#C85A32] shrink-0" />
                  {o.signature}
                </p>
              </div>
            </div>
            <div className="flex flex-col lg:items-end lg:justify-center gap-6">
              <div className="flex flex-wrap gap-2 lg:justify-end">
                {["Dine in", "Take away", "Celebration cakes"].map((t) => (
                  <span
                    key={t}
                    className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-[#1C130E]/60 border border-[rgba(28,19,14,0.2)] rounded-full px-4 py-2"
                  >
                    {t}
                  </span>
                ))}
              </div>
              <a
                href={MAP_DIRECTIONS_URL}
                target="_blank"
                rel="noreferrer"
                data-testid={`outlet-directions-${o.id}`}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#1C130E] text-[#FDFBF7] px-8 py-4 text-sm font-semibold hover:bg-[#C85A32] transition-colors"
              >
                Get directions
                <ExternalLink size={15} className="transition-transform duration-300 group-hover:translate-x-0.5" />
              </a>
            </div>
          </div>
        </Reveal>

        <Reveal>
          <div className="mt-16 lg:mt-20 rounded-3xl bg-[#1C130E] text-[#FDFBF7] px-8 py-12 lg:px-16 lg:py-16 flex flex-col lg:flex-row lg:items-center gap-8 justify-between overflow-hidden relative">
            <div className="max-w-xl">
              <p className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-[#D4A359] mb-4">
                Franchise Opportunities
              </p>
              <h3 className="font-display font-medium text-3xl sm:text-4xl lg:text-5xl leading-tight">
                Your city could be <span className="italic text-[#C85A32]">next.</span>
              </h3>
              <p className="mt-4 text-sm sm:text-base text-[#FDFBF7]/70 leading-relaxed">
                We're welcoming new franchise partners. One recipe book, full training, and butter — always included.
              </p>
            </div>
            <a
              href="#contact"
              data-testid="outlets-franchise-cta"
              onClick={(e) => {
                e.preventDefault();
                scrollToId("#contact");
              }}
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#C85A32] text-[#FDFBF7] px-8 py-4 text-sm font-semibold hover:bg-[#B04B26] transition-colors shrink-0"
            >
              Franchise enquiry
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:rotate-45" />
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
